import { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL, formatApiError, tokenStore } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Camera, ClipboardCheck, LogIn, LogOut, ShieldCheck, Trash2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Boat inspection photo documentation, split by check-in vs check-out.
 * Every photo gets a server-side timestamp so both parties agree on when
 * the picture was taken. Purely documentary – no admin approval required.
 */
export default function CustomerBoatInspection({ lang, booking }) {
  const T = (no, en) => (lang === "en" ? en : no);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingKind, setUploadingKind] = useState("");
  const [pendingNote, setPendingNote] = useState({ checkin: "", checkout: "" });
  const inRef = useRef(null);
  const outRef = useRef(null);

  const load = async () => {
    if (!booking?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/customer/boat-inspection/${booking.id}`);
      setPhotos(r.data || []);
    } catch {
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [booking?.id]);

  const today = new Date().toISOString().slice(0, 10);
  const startDate = booking?.start_date?.slice(0, 10);
  const endDate = booking?.end_date?.slice(0, 10);
  const showCheckin = !!booking && (today <= endDate);
  const showCheckout = !!booking && (today >= startDate);

  const upload = async (kind, files) => {
    if (!files?.length) return;
    setUploadingKind(kind);
    const note = pendingNote[kind] || "";
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("booking_id", booking.id);
        fd.append("kind", kind);
        if (note) fd.append("note", note);
        await api.post("/customer/boat-inspection/photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(
        T(
          "Bildene er lagret med tidsstempel.",
          "Photos saved with timestamp.",
        ),
      );
      setPendingNote((p) => ({ ...p, [kind]: "" }));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploadingKind("");
    }
  };

  const remove = async (photoId) => {
    if (!window.confirm(T("Fjerne bildet?", "Remove this photo?"))) return;
    try {
      await api.delete(`/customer/boat-inspection/photo/${photoId}`);
      toast.success(T("Bilde fjernet.", "Photo removed."));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const raw = (id) =>
    `${BACKEND_URL}/api/boat-inspection/photo/${id}/raw?auth=${encodeURIComponent(tokenStore.get())}`;

  const fmtTs = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString(lang === "en" ? "en-GB" : "nb-NO", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const renderSection = (kind) => {
    const kindPhotos = photos.filter((p) => p.kind === kind);
    const isCheckin = kind === "checkin";
    const Icon = isCheckin ? LogIn : LogOut;
    const ref = isCheckin ? inRef : outRef;
    const uploading = uploadingKind === kind;
    return (
      <div
        data-testid={`inspection-section-${kind}`}
        className="rounded-2xl border border-[#BD984D]/25 bg-white p-5"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-full bg-[#BD984D]/15 flex items-center justify-center">
            <Icon className="w-5 h-5 text-[#A57F39]" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-[#A57F39] font-semibold">
              {isCheckin
                ? T("Ved innsjekk", "At check-in")
                : T("Ved utsjekk", "At check-out")}
            </div>
            <div className="text-lg font-semibold text-[#0C1824]">
              {isCheckin
                ? T("Hvordan så båten ut da du kom?", "How did the boat look when you arrived?")
                : T("Hvordan ser båten ut nå?", "How does the boat look now?")}
            </div>
          </div>
        </div>
        <p className="text-sm text-[#2B3F4D] leading-relaxed">
          {isCheckin
            ? T(
                "Ta noen bilder rundt båten når du kommer ombord. Da har både du og vi en felles referanse hvis noe skulle skje.",
                "Take a few photos around the boat when you arrive. That way you and we have a shared reference in case anything happens.",
              )
            : T(
                "Ta bilder før du forlater båten, så både du og vi vet nøyaktig hvordan den ble levert.",
                "Take photos before you leave the boat so you and we know exactly the condition it was returned in.",
              )}
        </p>

        <Textarea
          value={pendingNote[kind] || ""}
          onChange={(e) =>
            setPendingNote((p) => ({ ...p, [kind]: e.target.value.slice(0, 400) }))
          }
          rows={2}
          placeholder={T(
            "Kort notat (valgfritt), f.eks. \"Riper på styrbord side ved akter\"",
            "Short note (optional), e.g. \"Scratches on starboard rear\"",
          )}
          className="mt-3"
          data-testid={`inspection-note-${kind}`}
        />

        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            onClick={() => ref.current?.click()}
            disabled={uploading}
            data-testid={`inspection-upload-${kind}`}
            className="bg-[#BD984D] hover:bg-[#A57F39] text-white h-11"
          >
            <Camera className="w-4 h-4 mr-2" />
            {uploading
              ? T("Laster opp…", "Uploading…")
              : T("Ta bilde", "Take photo")}
          </Button>
        </div>
        <input
          ref={ref}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          onChange={(e) => upload(kind, e.target.files)}
        />

        {kindPhotos.length > 0 && (
          <div
            className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3"
            data-testid={`inspection-photos-${kind}`}
          >
            {kindPhotos.map((p) => (
              <div
                key={p.id}
                className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50 aspect-square group"
              >
                <img
                  src={raw(p.id)}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] px-1.5 py-1 flex items-center gap-1">
                  <ClipboardCheck className="w-3 h-3 text-[#ECC974]" />
                  <span className="truncate">{fmtTs(p.taken_at)}</span>
                </div>
                <button
                  type="button"
                  onClick={() => remove(p.id)}
                  data-testid={`inspection-photo-remove-${p.id}`}
                  className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                  aria-label={T("Fjern", "Remove")}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
                {p.note && (
                  <div className="absolute top-1.5 left-1.5 max-w-[80%] bg-[#0C1824]/75 text-white text-[10px] px-1.5 py-1 rounded line-clamp-2">
                    {p.note}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!booking) return null;

  return (
    <Card className="p-6 sm:p-8" data-testid="customer-boat-inspection">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-[#A57F39]" />
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#A57F39]">
          {T("Båtens tilstand", "Boat condition")}
        </div>
      </div>
      <h2 className="text-xl font-semibold text-[#0C1824]">
        {T(
          "Dokumenter båten – for din og vår trygghet",
          "Document the boat — for your peace of mind and ours",
        )}
      </h2>
      <p className="text-sm text-[#2B3F4D] mt-1">
        {T(
          "Bildene er kun synlige for deg og teamet vårt. Hvert bilde får automatisk et tidsstempel.",
          "Photos are only visible to you and our team. Each photo is automatically timestamped.",
        )}
      </p>

      <div className="mt-5 space-y-4">
        {showCheckin && renderSection("checkin")}
        {showCheckout && renderSection("checkout")}
        {loading && photos.length === 0 && (
          <div className="text-sm text-slate-500">
            {T("Laster bilder…", "Loading photos…")}
          </div>
        )}
      </div>
    </Card>
  );
}
